import { useEffect, useState, useRef, useCallback } from "react";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import {
  Plus, Code2, Eye, Rocket, Copy, ExternalLink, Trash2, History, Settings as SettingsIcon,
  Files, Loader2, ArrowLeft, Save, Monitor, Tablet, Smartphone, Maximize2, RotateCcw, Ban, Upload,
} from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ACCESS = [
  ["off", "Off"], ["admin_only", "Admin only"], ["internal_testers", "Internal testers"],
  ["specific_users", "Specific users"], ["all_users", "All users"],
];
const STATUS_COLOR = {
  draft: "text-[#A1A1AA]", testing: "text-[#f59e0b]", ready: "text-[#38bdf8]",
  live: "text-[#22c55e]", disabled: "text-[#ef4444]", rolled_back: "text-[#f59e0b]",
};
const CODE_TABS = [
  { key: "html", label: "HTML", lang: "html", phase1: true },
  { key: "css", label: "CSS", lang: "css", phase1: true },
  { key: "javascript", label: "JavaScript", lang: "javascript", phase1: true },
  { key: "react", label: "React JS", lang: "javascript", phase1: false, note: "Phase 2 — in-browser compile" },
  { key: "python", label: "Python", lang: "python", phase1: false, note: "Phase 2 — Pyodide runtime required" },
  { key: "java", label: "Java", lang: "java", phase1: false, note: "Browser runtime unavailable" },
];

// section="pages" powers the Admin "Web Page" tab (public /pages directory); default = Web Design tab.
export default function WebDesign({ section = "design" }) {
  const [view, setView] = useState("list");
  const [pageId, setPageId] = useState(null);
  return view === "list"
    ? <PageList section={section} onOpen={(id) => { setPageId(id); setView("editor"); }} />
    : <PageEditor pageId={pageId} onBack={() => setView("list")} />;
}

// ---------------- LIST / DASHBOARD ----------------
function PageList({ onOpen, section }) {
  const [pages, setPages] = useState(null);
  const [ov, setOv] = useState(null);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try { setPages((await api.wdList(q, section)).pages); setOv(await api.wdOverview(section)); }
    catch { toast.error("Failed to load web pages"); }
  }, [q, section]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail?.message || "Action failed"); } };
  const copyUrl = (slug) => { navigator.clipboard?.writeText(`${window.location.origin}/web/${slug}`); toast.success("URL copied"); };

  return (
    <div data-testid={section === "pages" ? "web-page-admin" : "web-design"}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-white">{section === "pages" ? "Web Page" : "Web Design"}</h2>
          <p className="text-sm text-[#A1A1AA]">{section === "pages" ? <>Pages published here are listed for visitors at <a href="/pages" target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">/pages</a> with search, thumbnail and summary. Visibility follows each page's access level.</> : "Create, edit, preview, publish and manage custom web pages directly from the Admin Dashboard."}</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="wd-new-btn" className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black hover:bg-[#00d0dd]"><Plus size={16} /> New Web Page</button>
      </div>

      {ov && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-6" data-testid="wd-overview">
          {[["Total", ov.total_pages], ["Live", ov.live_pages], ["Drafts", ov.draft_pages], ["Views", ov.total_views], ["Runtime errors", ov.runtime_errors], ["Last published", ov.last_published?.name || "—"]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-white/10 bg-[#0D0D0E] p-3"><div className="text-[11px] uppercase tracking-widest text-[#A1A1AA]">{l}</div><div className="truncate text-lg font-bold text-white">{v}</div></div>
          ))}
        </div>
      )}

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages…" data-testid="wd-search" className="mb-3 w-full max-w-xs rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white" />

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead className="bg-white/5 text-[11px] uppercase tracking-wider text-[#A1A1AA]">
            <tr>{["Name", "Slug", "Full URL", "Status", "Access", "Plan", "Ver", "Live", "Updated", "Actions"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {pages == null ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center"><Loader2 className="mx-auto animate-spin text-[#00F0FF]" /></td></tr>
            ) : pages.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-[#A1A1AA]">No pages yet. Create your first web page.</td></tr>
            ) : pages.map((p) => (
              <tr key={p.id} className="border-t border-white/5 text-white/90" data-testid={`wd-row-${p.id}`}>
                <td className="px-3 py-2 font-semibold">{p.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.slug}</td>
                <td className="px-3 py-2 font-mono text-xs text-[#00F0FF]">/web/{p.slug}</td>
                <td className={`px-3 py-2 font-semibold uppercase ${STATUS_COLOR[p.status] || "text-white"}`}>{p.status}</td>
                <td className="px-3 py-2 text-xs">{(ACCESS.find((a) => a[0] === p.access_level) || [])[1] || p.access_level}</td>
                <td className="px-3 py-2 text-xs uppercase">{p.plan}</td>
                <td className="px-3 py-2 text-xs">v{p.current_version}</td>
                <td className="px-3 py-2 text-xs">{p.live_version ? `v${p.live_version}` : "—"}</td>
                <td className="px-3 py-2 text-xs text-[#A1A1AA]">{(p.updated_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <IconBtn t="Edit/Code" testid={`wd-edit-${p.id}`} onClick={() => onOpen(p.id)}><Code2 size={14} /></IconBtn>
                    {p.live_version && <IconBtn t="Open live" testid={`wd-open-${p.id}`} onClick={() => window.open(`/web/${p.slug}`, "_blank")}><ExternalLink size={14} /></IconBtn>}
                    {p.live_version && <IconBtn t="Copy URL" testid={`wd-copy-${p.id}`} onClick={() => copyUrl(p.slug)}><Copy size={14} /></IconBtn>}
                    {p.status !== "live" ? (
                      <IconBtn t="Publish" testid={`wd-publish-${p.id}`} onClick={() => act(() => api.wdPublish(p.id), "Published")}><Rocket size={14} className="text-[#22c55e]" /></IconBtn>
                    ) : (
                      <IconBtn t="Unpublish" testid={`wd-unpublish-${p.id}`} onClick={() => act(() => api.wdUnpublish(p.id), "Unpublished")}><Ban size={14} className="text-[#f59e0b]" /></IconBtn>
                    )}
                    <IconBtn t="Duplicate" testid={`wd-duplicate-${p.id}`} onClick={() => act(() => api.wdDuplicate(p.id), "Duplicated")}><Files size={14} /></IconBtn>
                    <IconBtn t="Delete" testid={`wd-delete-${p.id}`} onClick={() => setConfirmDel(p)}><Trash2 size={14} className="text-[#ef4444]" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewPageModal section={section} onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); onOpen(id); }} />}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="border-white/10 bg-[#0D0D0E] text-white">
          <DialogTitle>Delete “{confirmDel?.name}”?</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">{confirmDel?.status === "live" ? "This page is LIVE — unpublish it first." : "This permanently deletes the page and all its versions and assets."}</DialogDescription>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-white/15 px-3 py-2 text-sm">Cancel</button>
            <button data-testid="wd-delete-confirm" disabled={confirmDel?.status === "live"} onClick={() => act(() => api.wdDelete(confirmDel.id), "Deleted").then(() => setConfirmDel(null))} className="rounded-lg bg-[#ef4444] px-3 py-2 text-sm font-bold text-white disabled:opacity-40">Delete</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IconBtn({ children, onClick, t, testid }) {
  return <button title={t} data-testid={testid} onClick={onClick} className="rounded-md border border-white/10 p-1.5 text-white/70 hover:bg-white/10 hover:text-white">{children}</button>;
}

function NewPageModal({ onClose, onCreated, section }) {
  const [f, setF] = useState({ name: "", slug: "", description: "", access_level: "admin_only", plan: "free" });
  const [busy, setBusy] = useState(false);
  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  const create = async () => {
    if (!f.name.trim()) { toast.error("Enter a page name"); return; }
    setBusy(true);
    try { const r = await api.wdCreate({ ...f, slug: f.slug || slugify(f.name), section }); toast.success("Page created"); onCreated(r.page.id); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Create failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#0D0D0E] text-white" data-testid="wd-new-modal">
        <DialogTitle>New Web Page</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">Give your page a name and a unique URL slug.</DialogDescription>
        <div className="mt-2 space-y-3">
          <Field label="Page Name"><input data-testid="wd-new-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: f.slug || slugify(e.target.value) })} className="wd-inp" /></Field>
          <Field label="URL Slug"><div className="flex items-center gap-2"><span className="text-xs text-[#A1A1AA]">/web/</span><input data-testid="wd-new-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} className="wd-inp" /></div></Field>
          <Field label="Description"><input data-testid="wd-new-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="wd-inp" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Access Level"><select data-testid="wd-new-access" value={f.access_level} onChange={(e) => setF({ ...f, access_level: e.target.value })} className="wd-inp">{ACCESS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
            <Field label="Plan"><select data-testid="wd-new-plan" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} className="wd-inp"><option value="free">Free</option><option value="pro">Pro</option></select></Field>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-3 py-2 text-sm">Cancel</button>
          <button data-testid="wd-new-create" disabled={busy} onClick={create} className="rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black">{busy ? "Creating…" : "Create Page"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => <label className="block"><span className="mb-1 block text-[11px] uppercase tracking-widest text-[#A1A1AA]">{label}</span>{children}</label>;

// ---------------- EDITOR ----------------
function PageEditor({ pageId, onBack }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("html");
  const [code, setCode] = useState({ html: "", css: "", javascript: "" });
  const [mode, setMode] = useState("split"); // code | split | preview
  const [device, setDevice] = useState("desktop");
  const [preview, setPreview] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [busy, setBusy] = useState("");
  const [showVersions, setShowVersions] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAssets, setShowAssets] = useState(false);
  const [confirmPub, setConfirmPub] = useState(false);
  const autosaveRef = useRef(null);

  const load = useCallback(async () => {
    const d = await api.wdGet(pageId); setData(d);
    setCode({ html: d.version?.html || "", css: d.version?.css || "", javascript: d.version?.javascript || "" });
  }, [pageId]);
  useEffect(() => { load(); }, [load]);

  const doPreview = useCallback(async () => {
    try { const r = await api.wdPreview(pageId, code); setPreview(r.document); } catch { toast.error("Preview failed"); }
  }, [pageId, code]);
  useEffect(() => { if (data && mode !== "code") doPreview(); }, [data, mode]); // eslint-disable-line

  const saveDraft = async (silent) => {
    setBusy("save");
    try { const r = await api.wdSave(pageId, code); setSavedAt(r.saved_at); if (!silent) toast.success(`Saved v${r.version_number}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Save failed"); }
    setBusy("");
  };
  // debounced autosave
  useEffect(() => {
    if (!data) return;
    if (autosaveRef.current) clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => { api.wdSave(pageId, code).then((r) => setSavedAt(r.saved_at)).catch(() => {}); }, 2500);
    return () => clearTimeout(autosaveRef.current);
  }, [code]); // eslint-disable-line

  const publish = async () => {
    setBusy("pub"); setConfirmPub(false);
    try { await api.wdSave(pageId, code); await api.wdPublish(pageId); toast.success("Published live"); await load(); }
    catch (e) { const dt = e?.response?.data?.detail; toast.error(dt?.message || (dt?.errors ? dt.errors[0]?.message : "Publish failed")); }
    setBusy("");
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  const p = data.page;
  const srcUrl = `${window.location.origin}/web/${p.slug}`;
  const dw = device === "mobile" ? 390 : device === "tablet" ? 768 : "100%";
  const curTab = CODE_TABS.find((t) => t.key === tab);

  return (
    <div data-testid="wd-editor">
      <style>{`.wd-inp{width:100%;border-radius:.5rem;border:1px solid rgba(255,255,255,.15);background:rgba(0,0,0,.4);padding:.5rem .75rem;font-size:.85rem;color:#fff}`}</style>
      {/* top bar */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} data-testid="wd-back" className="rounded-lg border border-white/15 p-2 text-white/70 hover:bg-white/10"><ArrowLeft size={16} /></button>
          <div>
            <div className="font-display text-base font-bold text-white">{p.name} <span className={`ml-2 text-xs font-semibold uppercase ${STATUS_COLOR[p.status]}`}>{p.status}</span></div>
            <div className="font-mono text-xs text-[#A1A1AA]">/web/{p.slug} · v{p.current_version}{p.live_version ? ` · live v${p.live_version}` : ""} {savedAt && <span className="ml-2 text-[#22c55e]">Draft saved</span>}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowVersions(true)} data-testid="wd-versions-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5"><History size={14} /> Versions</button>
          <button onClick={() => setShowAssets(true)} data-testid="wd-assets-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5"><Files size={14} /> Assets</button>
          <button onClick={() => setShowSettings(true)} data-testid="wd-settings-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5"><SettingsIcon size={14} /> Settings</button>
          <button onClick={() => saveDraft(false)} disabled={busy === "save"} data-testid="wd-save-btn" className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"><Save size={14} /> Save</button>
          {p.status === "live"
            ? <button onClick={() => api.wdUnpublish(pageId).then(() => { toast.success("Unpublished"); load(); })} data-testid="wd-unpublish-btn" className="flex items-center gap-1.5 rounded-lg border border-[#f59e0b]/40 px-3 py-1.5 text-xs font-bold text-[#f59e0b]"><Ban size={14} /> Unpublish</button>
            : null}
          <button onClick={() => setConfirmPub(true)} disabled={busy === "pub"} data-testid="wd-publish-btn" className="flex items-center gap-1.5 rounded-lg bg-[#22c55e] px-3 py-1.5 text-xs font-bold text-black"><Rocket size={14} /> Publish</button>
        </div>
      </div>

      {/* mode toggle */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-1 rounded-lg border border-white/10 p-1">
          {["code", "split", "preview"].map((m) => <button key={m} onClick={() => setMode(m)} data-testid={`wd-mode-${m}`} className={`rounded-md px-3 py-1 text-xs font-semibold capitalize ${mode === m ? "bg-[#00F0FF] text-black" : "text-white/70"}`}>{m}</button>)}
        </div>
        {mode !== "code" && (
          <div className="flex items-center gap-2">
            <button onClick={() => setDevice("desktop")} className={device === "desktop" ? "text-[#00F0FF]" : "text-white/50"} data-testid="wd-dev-desktop"><Monitor size={16} /></button>
            <button onClick={() => setDevice("tablet")} className={device === "tablet" ? "text-[#00F0FF]" : "text-white/50"} data-testid="wd-dev-tablet"><Tablet size={16} /></button>
            <button onClick={() => setDevice("mobile")} className={device === "mobile" ? "text-[#00F0FF]" : "text-white/50"} data-testid="wd-dev-mobile"><Smartphone size={16} /></button>
            <button onClick={doPreview} className="rounded-lg border border-white/15 px-2 py-1 text-xs text-white/70" data-testid="wd-refresh-preview">Refresh</button>
            <button onClick={() => window.open(srcUrl, "_blank")} className="rounded-lg border border-white/15 px-2 py-1 text-xs text-white/70" data-testid="wd-open-preview"><Maximize2 size={13} /></button>
          </div>
        )}
      </div>

      <div className={`grid gap-3 ${mode === "split" ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {mode !== "preview" && (
          <div className="rounded-xl border border-white/10 bg-[#0D0D0E]">
            <div className="flex flex-wrap gap-1 border-b border-white/10 p-2">
              {CODE_TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`wd-tab-${t.key}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold ${tab === t.key ? "bg-[#00F0FF] text-black" : "text-white/70 hover:bg-white/5"} ${!t.phase1 ? "opacity-70" : ""}`}>
                  {t.label}{!t.phase1 && <span className="ml-1 text-[9px] text-[#f59e0b]">•</span>}
                </button>
              ))}
              <button onClick={() => setShowAssets(true)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-white/70 hover:bg-white/5">Assets</button>
              <button onClick={() => setShowSettings(true)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-white/70 hover:bg-white/5">Settings</button>
            </div>
            {curTab?.phase1 ? (
              <Editor
                height="60vh"
                language={curTab.lang}
                theme="vs-dark"
                value={code[tab]}
                onChange={(v) => setCode((c) => ({ ...c, [tab]: v ?? "" }))}
                options={{ minimap: { enabled: false }, fontSize: 13, lineNumbers: "on", automaticLayout: true, tabSize: 2, wordWrap: "on" }}
              />
            ) : (
              <div className="flex h-[60vh] flex-col items-center justify-center gap-3 text-center" data-testid={`wd-placeholder-${tab}`}>
                <Code2 size={40} className="text-white/20" />
                <div className="text-lg font-bold text-white">{curTab.label}</div>
                <div className="rounded-full bg-[#f59e0b]/15 px-3 py-1 text-xs font-semibold text-[#f59e0b]">{curTab.note}</div>
                <p className="max-w-sm text-xs text-[#A1A1AA]">This language is a Phase 2 placeholder. It is not executable in Phase 1 and never runs on the server. HTML, CSS and JavaScript are fully functional now.</p>
              </div>
            )}
          </div>
        )}
        {mode !== "code" && (
          <div className="overflow-hidden rounded-xl border border-white/10 bg-white" data-testid="wd-preview-pane">
            <div className="mx-auto h-[62vh] overflow-auto" style={{ width: dw, maxWidth: "100%" }}>
              <iframe title="preview" srcDoc={preview} sandbox="allow-scripts allow-popups allow-forms" referrerPolicy="no-referrer" className="h-[62vh] w-full border-0" data-testid="wd-preview-frame" />
            </div>
          </div>
        )}
      </div>

      {showVersions && <VersionsModal pageId={pageId} onClose={() => setShowVersions(false)} onChanged={load} />}
      {showSettings && <SettingsModal page={p} onClose={() => setShowSettings(false)} onSaved={load} />}
      {showAssets && <AssetsModal pageId={pageId} onClose={() => setShowAssets(false)} />}

      <Dialog open={confirmPub} onOpenChange={(o) => !o && setConfirmPub(false)}>
        <DialogContent className="border-white/10 bg-[#0D0D0E] text-white" data-testid="wd-publish-modal">
          <DialogTitle>Publish “{p.name}”?</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">This validates the code and makes the current version live at /web/{p.slug}.</DialogDescription>
          <div className="mt-2 space-y-1 text-sm">
            <div>Slug: <span className="font-mono text-[#00F0FF]">/web/{p.slug}</span></div>
            <div>Access: {(ACCESS.find((a) => a[0] === p.access_level) || [])[1]} · Plan: {p.plan.toUpperCase()}</div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setConfirmPub(false)} className="rounded-lg border border-white/15 px-3 py-2 text-sm">Cancel</button>
            <button onClick={publish} data-testid="wd-publish-confirm" className="rounded-lg bg-[#22c55e] px-4 py-2 text-sm font-bold text-black">Publish Live</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VersionsModal({ pageId, onClose, onChanged }) {
  const [vs, setVs] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.wdVersions(pageId).then(setVs);
  useEffect(() => { load(); }, []); // eslint-disable-line
  const rollback = async (vid) => { try { await api.wdRollback(pageId, vid, reason); toast.success("Rolled back"); onChanged(); load(); } catch { toast.error("Rollback failed"); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-white/10 bg-[#0D0D0E] text-white" data-testid="wd-versions-modal">
        <DialogTitle>Version history</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">Rolling back makes an older published version live again. No version is ever deleted.</DialogDescription>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Rollback reason (optional)" className="wd-inp mt-2" />
        <div className="mt-3 max-h-[50vh] space-y-2 overflow-auto">
          {vs?.versions?.map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded-lg border border-white/10 p-2 text-sm" data-testid={`wd-version-${v.version_number}`}>
              <div><span className="font-bold">v{v.version_number}</span> <span className={`ml-2 text-xs uppercase ${STATUS_COLOR[v.status]}`}>{v.status}</span><div className="text-xs text-[#A1A1AA]">{v.change_summary} · {(v.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
              {v.status !== "live" && <button onClick={() => rollback(v.id)} data-testid={`wd-rollback-${v.version_number}`} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/80 hover:bg-white/5"><RotateCcw size={13} /> Make live</button>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsModal({ page, onClose, onSaved }) {
  const [f, setF] = useState({
    name: page.name, description: page.description || "", access_level: page.access_level, plan: page.plan,
    seo: page.seo || {}, security: page.security || { external_policy: "approved", allowed_domains: [] },
    eligible_users: (page.eligible_users || []).join(", "), thumbnail_url: page.thumbnail_url || "",
  });
  const [assets, setAssets] = useState([]);
  useEffect(() => { if (page.section === "pages") api.wdAssets(page.id).then((r) => setAssets(r.assets.filter((a) => (a.mime_type || "").startsWith("image/")))).catch(() => {}); }, [page.id, page.section]);
  const save = async () => {
    try {
      await api.wdUpdateSettings(page.id, { name: f.name, description: f.description, access_level: f.access_level, plan: f.plan, seo: f.seo, security: { ...f.security, allowed_domains: (f.security.allowed_domains || []) }, eligible_users: f.eligible_users.split(",").map((s) => s.trim()).filter(Boolean), thumbnail_url: f.thumbnail_url });
      toast.success("Settings saved"); onSaved(); onClose();
    } catch { toast.error("Save failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-white/10 bg-[#0D0D0E] text-white" data-testid="wd-settings-modal">
        <DialogTitle>Page settings</DialogTitle>
        <div className="mt-2 max-h-[60vh] space-y-3 overflow-auto pr-1">
          <Field label="Page Name"><input className="wd-inp" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Description"><input className="wd-inp" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Access Level"><select data-testid="wd-set-access" className="wd-inp" value={f.access_level} onChange={(e) => setF({ ...f, access_level: e.target.value })}>{ACCESS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
            <Field label="Plan"><select data-testid="wd-set-plan" className="wd-inp" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}><option value="free">Free</option><option value="pro">Pro</option></select></Field>
          </div>
          {page.section === "pages" && (
            <Field label="List thumbnail">
              <select data-testid="wd-set-thumbnail" className="wd-inp" value={f.thumbnail_url} onChange={(e) => setF({ ...f, thumbnail_url: e.target.value })}>
                <option value="">Auto — first image in the page</option>
                {assets.map((a) => <option key={a.id} value={a.url}>{a.name}</option>)}
              </select>
              <span className="mt-1 block text-[11px] text-[#A1A1AA]">Upload images under Assets to pick one here.</span>
            </Field>
          )}
          {f.access_level === "specific_users" && <Field label="Eligible user emails (comma-separated)"><input className="wd-inp" value={f.eligible_users} onChange={(e) => setF({ ...f, eligible_users: e.target.value })} /></Field>}
          <Field label="SEO Title"><input className="wd-inp" value={f.seo.title || ""} onChange={(e) => setF({ ...f, seo: { ...f.seo, title: e.target.value } })} /></Field>
          <Field label="SEO Description"><input className="wd-inp" value={f.seo.description || ""} onChange={(e) => setF({ ...f, seo: { ...f.seo, description: e.target.value } })} /></Field>
          <Field label="External Resource Policy"><select className="wd-inp" value={f.security.external_policy} onChange={(e) => setF({ ...f, security: { ...f.security, external_policy: e.target.value } })}><option value="none">No external resources</option><option value="approved">Approved domains only</option></select></Field>
          <Field label="Allowed Domains (comma-separated)"><input className="wd-inp" value={(f.security.allowed_domains || []).join(", ")} onChange={(e) => setF({ ...f, security: { ...f.security, allowed_domains: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })} /></Field>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-3 py-2 text-sm">Cancel</button>
          <button onClick={save} data-testid="wd-settings-save" className="rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black">Save Settings</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssetsModal({ pageId, onClose }) {
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.wdAssets(pageId).then((r) => setAssets(r.assets));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const upload = async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); try { await api.wdUploadAsset(pageId, f); toast.success("Uploaded"); load(); } catch (er) { toast.error(er?.response?.data?.detail?.message || "Upload failed"); } setBusy(false); };
  const copy = (a) => { navigator.clipboard?.writeText(`${window.location.origin}/api${a.url}`); toast.success("Asset URL copied"); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-white/10 bg-[#0D0D0E] text-white" data-testid="wd-assets-modal">
        <DialogTitle>Assets</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">Uploaded assets get a safe URL you can reference in your page code.</DialogDescription>
        <label className="mt-2 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/20 p-3 text-sm text-white/70 hover:border-[#00F0FF]/50" data-testid="wd-asset-upload-label"><Upload size={16} /> {busy ? "Uploading…" : "Upload asset"}<input type="file" className="hidden" onChange={upload} data-testid="wd-asset-upload" /></label>
        <div className="mt-3 max-h-[45vh] space-y-2 overflow-auto">
          {assets.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-lg border border-white/10 p-2 text-sm">
              <div className="min-w-0"><div className="truncate font-semibold">{a.name}</div><div className="truncate font-mono text-[11px] text-[#A1A1AA]">/api{a.url}</div></div>
              <div className="flex gap-1"><IconBtn t="Copy URL" onClick={() => copy(a)}><Copy size={13} /></IconBtn><IconBtn t="Delete" onClick={() => api.wdDeleteAsset(a.id).then(load)}><Trash2 size={13} className="text-[#ef4444]" /></IconBtn></div>
            </div>
          ))}
          {assets.length === 0 && <div className="py-4 text-center text-xs text-[#A1A1AA]">No assets yet.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
